import moment from "moment";
import sortBy from "lodash/sortBy";
import {
  BRANDS_HAVE_DRILL_FEATURE,
  DRILL_LEVELS,
  FILTERS,
} from "@/config/constants";

type CalendarDate = {
  year: number;
  month: number;
  day?: number;
  date?: number;
};

type ChartDataRow = Record<string, string | number | null | undefined>;

type ReportRange = {
  from: {
    year: string | number;
    month: string | number;
    date: string | number;
  };
  to: { year: string | number; month: string | number; date: string | number };
};

type DrillValues = {
  filter?: string;
  customRange?: [string, string] | string[];
};

export const parseDateString = (dateString: string) => {
  const [year, month, day] = dateString.split("-").map(Number);
  return { year, month, day };
};

export const formatDate = (date?: CalendarDate | null) => {
  if (date) {
    const year = date.year;
    const month = String(date.month).padStart(2, "0");
    const day = String(date.day).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return "";
};

export const formatAndSortData = (
  data: ChartDataRow[] | null | undefined,
  labels: string[],
  currentDrillLevel: string
) => {
  let formatData: Array<{ x: string | number | null | undefined; y: unknown }> =
    [];

  formatData =
    data &&
    data.map((d) => {
      let formattedX = d[labels[0]];
      let formattedY = d[labels[1]];

      if (currentDrillLevel === "quarter") {
        formattedX = d[labels[0]];
      } else if (currentDrillLevel === "year") {
        formattedX = d[labels[0]];
      } else if (currentDrillLevel === "day") {
        formattedX = moment(d[labels[0]]).format("YYYY-MM-DD");
      } else {
        formattedX = moment(d[labels[0]], [
          "YYYY-M",
          "YYYY-MM",
          "YYYY-MM-DD",
        ]).format("YYYY-MM-DD");
      }

      return { x: formattedX, y: formattedY };
    });

  // Sorting data as per drill level selected
  if (currentDrillLevel === "month" || currentDrillLevel === "day") {
    formatData = sortBy(formatData, (o) => moment(o.x, "YYYY-MM-DD"));
  } else if (currentDrillLevel === "quarter") {
    formatData = sortBy(formatData, (o) => {
      const [quarter, year] = String(o.x).split("-");
      return `${year}-${quarter}`;
    });
  } else if (currentDrillLevel === "year") {
    formatData = sortBy(formatData, "x");
  }

  return formatData;
};

export const isDateRangeOutOfBounds = (
  values: DrillValues,
  initialRange: ReportRange | null | undefined,
  drillLevel: string
) => {
  const isCustomRange = values.filter === FILTERS.CUSTOM;

  const formatDate = (
    year: string | number,
    month: string | number,
    day: string | number
  ) =>
    `${year}-${month.toString().padStart(2, "0")}-${day
      .toString()
      .padStart(2, "0")}`;

  const isDateRangeOutOfRange =
    isCustomRange && values.customRange
      ? moment(values.customRange[1]).diff(
          moment(values.customRange[0]),
          "days"
        ) > 60
      : false;

  const isInitialDateRangeOutOfRange = initialRange
    ? moment(
        formatDate(
          initialRange.to.year,
          initialRange.to.month,
          initialRange.to.date
        )
      ).diff(
        moment(
          formatDate(
            initialRange.from.year,
            initialRange.from.month,
            initialRange.from.date
          )
        ),
        "days"
      ) > 60
    : false;

  if (
    drillLevel === DRILL_LEVELS.MONTH &&
    (isDateRangeOutOfRange || isInitialDateRangeOutOfRange)
  ) {
    return true;
  } else if (values.filter === FILTERS.YTD) {
    const startOfYear = moment().startOf("year");
    const today = moment();
    const daysDifference = today.diff(startOfYear, "days");
    return drillLevel === DRILL_LEVELS.MONTH && daysDifference > 60;
  } else if (values.filter === FILTERS.LAST_QUARTER) {
    return drillLevel === DRILL_LEVELS.MONTH;
  } else {
    return false;
  }
};

export const getInitialRange = (
  initialRange: ReportRange | null | undefined,
  today: moment.Moment
) => {
  const validInitialRange =
    initialRange && initialRange.from && initialRange.to;
  const initialStart = validInitialRange
    ? moment(
        `${initialRange.from.year}-${initialRange.from.month}-${initialRange.from.date}`,
        "YYYY-MM-DD"
      )
    : today;
  const initialEnd = validInitialRange
    ? moment(
        `${initialRange.to.year}-${initialRange.to.month}-${initialRange.to.date}`,
        "YYYY-MM-DD"
      )
    : today;

  return [initialStart, initialEnd];
};

export const isBrandHasDrillFeature = (
  system_generated_brand_id?: string | null
) => {
  return (
    BRANDS_HAVE_DRILL_FEATURE?.includes("all") ||
    BRANDS_HAVE_DRILL_FEATURE?.includes(system_generated_brand_id)
  );
};
